"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, Tag, FolderTree, Scale, HelpCircle, Sparkles, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface IntentTab {
  id: string;
  label: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
  mappedPageType: string;
  explanation: string;
  approach: string;
  examples: string[];
}
const intentTabs: IntentTab[] = [{
  id: "product-specific",
  label: "Product-specific & long-tail",
  icon: Tag,
  mappedPageType: "Individual product pages or focused variant pages",
  explanation: "A shopper searching with high specificity usually knows precisely what they need, the required specifications, or the exact product model. These searches indicate strong commercial intent.",
  approach: "I map these terms directly to individual product pages or tightly focused variant pages. Competition on long-tail product queries is typically lower than on head terms, creating efficient conversion opportunities.",
  examples: ["Dyson V11 filter replacement", "leather office chair with lumbar support"]
}, {
  id: "category-level",
  label: "Category-level & transactional",
  icon: FolderTree,
  mappedPageType: "Category, subcategory & collection pages",
  explanation: "Broader commercial searches where the shopper has decided on the type of product but not the exact item. This is where non-branded organic sales are won.",
  approach: "These queries inform category and collection page naming, introductory on-page copy, and internal linking structures, giving search engines clear signals to rank your main commercial hubs.",
  examples: ["women's trail running shoes UK", "oak dining tables under £500"]
}, {
  id: "brand-comparison",
  label: "Brand vs non-brand & comparison",
  icon: Scale,
  mappedPageType: "Comparison pages, curated hubs & buying guides",
  explanation: "Shoppers evaluating alternative brands, models, or configurations before committing to a purchase. They are actively researching and comparing features, prices, and suitability.",
  approach: "I evaluate whether to build dedicated comparison pages or buying guides rather than forcing everything into product pages. This captures shoppers earlier in the journey and guides them towards your catalogue.",
  examples: ["Sage vs De'Longhi coffee machine", "best cordless drill for DIY"]
}, {
  id: "informational",
  label: "Informational supporting purchases",
  icon: HelpCircle,
  mappedPageType: "How-to guides, sizing resources & category FAQs",
  explanation: "Informational queries asked by customers who are one step away from making a purchase, seeking clarification on sizing, installation, or product selection.",
  approach: "I design informational resources that answer the exact pre-purchase question, then immediately guide the reader into the relevant categories, collections, and product recommendations.",
  examples: ["how to measure for blackout curtains", "what tog duvet for winter UK"]
}];
export default function KeywordIntent() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTabId, setActiveTabId] = useState<string>("product-specific");
  const activeTab = intentTabs.find(tab => tab.id === activeTabId) || intentTabs[0];
  const ActiveIcon = activeTab.icon;
  return <section id="keywordintent" data-nav="light" className="bg-muted/40 text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Search Intent Architecture
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            Ecommerce keyword research and search intent
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Effective keyword research aligns with search intent. In ecommerce,
            intent breaks into distinct types, each requiring a different page
            type and optimisation approach to capture shoppers at the right
            stage of their journey.
          </p>
        </div>

        {/* Tab Buttons (Real buttons with aria-pressed, keyboard-operable, wrap on mobile) */}
        <div className="mb-12">
          <div role="tablist" aria-label="Ecommerce search intent types" className="flex flex-wrap items-center justify-center gap-2 mb-8">
            {intentTabs.map((tab, i) => {
            const Icon = tab.icon;
            const isActive = tab.id === activeTabId;
            return <Button key={tab.id} data-index={i} type="button" role="tab" aria-selected={isActive} aria-pressed={isActive} aria-controls={`intent-panel-${tab.id}`} id={`intent-tab-${tab.id}`} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveTabId(tab.id)} className={`rounded-full px-4 sm:px-5 transition-all text-xs sm:text-sm font-medium flex items-center gap-2 ${isActive ? "shadow-xs" : "text-muted-foreground hover:text-foreground"}`}>
                  <Icon className="size-3.5" />
                  <span>{tab.label}</span>
                </Button>;
          })}
          </div>

          {/* Active Tab Detail Panel */}
          <motion.div key={activeTab.id} id={`intent-panel-${activeTab.id}`} role="tabpanel" aria-labelledby={`intent-tab-${activeTab.id}`} initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="max-w-4xl mx-auto">
            <Card className="border border-border/80 bg-card shadow-xs">
              <CardHeader className="pb-4 border-b border-border/60">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <ActiveIcon className="size-5" />
                    </div>
                    <div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                        Search Intent Type
                      </span>
                      <CardTitle className="text-xl font-semibold font-default text-foreground">
                        {activeTab.label}
                      </CardTitle>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground self-start sm:self-center font-mono">
                    Illustrative examples, not client data
                  </span>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                <div>
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                    What this search intent means
                  </h3>
                  <p className="text-sm sm:text-base text-foreground leading-relaxed">
                    {activeTab.explanation}
                  </p>
                </div>

                <div className="pt-4 border-t border-border/60">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                    Target page type & strategy
                  </h3>
                  <div className="p-3.5 rounded-md border border-border bg-background/60 mb-3">
                    <span className="text-xs font-semibold text-primary block mb-1">
                      Mapped Page Type:
                    </span>
                    <span className="text-sm font-medium text-foreground">
                      {activeTab.mappedPageType}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {activeTab.approach}
                  </p>
                </div>

                <div className="pt-4 border-t border-border/60">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                    Illustrative example searches
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {activeTab.examples.map((example, idx) => <div key={`example-${idx}`} data-index={idx} className="p-3.5 rounded-md border border-border bg-background/80 text-sm font-mono text-foreground flex items-center gap-2.5">
                        <Search className="size-3.5 text-primary shrink-0" />
                        <span className="truncate">&quot;{example}&quot;</span>
                      </div>)}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* How this feeds a keyword-to-page map */}
        <div className="max-w-4xl mx-auto mt-12 bg-card border border-border/80 rounded-2xl p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <div className="size-10 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
              <MapPin className="size-5" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg sm:text-xl font-semibold font-default text-foreground">
                How this feeds a keyword-to-page map
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                Intent mapping directly informs a master keyword-to-page map.
                This document assigns every viable search query to a single
                canonical URL, identifies where new collections or buying guides
                are required, and prevents keyword cannibalisation across product
                variants and category hierarchies.
              </p>
              <div className="pt-2">
                <Link to="/SeoAuditsPage" className="text-primary underline underline-offset-4 hover:text-primary/80 text-sm font-medium inline-flex items-center gap-1.5">
                  Learn how an audit reveals your keyword mapping gaps
                  <ArrowRight className="size-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}